/**
 * Auth Hook with Real-Time SSE Synchronization
 *
 * Provides auth state and actions with instant cross-tab and cross-service logout.
 *
 * Features:
 * - Server-Sent Events (SSE) for real-time auth state updates
 * - BroadcastChannel for same-origin cross-tab synchronization
 * - Automatic reconnection with exponential backoff
 * - Clean connection management and cleanup
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore';

const AUTH_HUB_URL = window.location.origin; // Use current origin

export function useAuth() {
  const { user, token, isAuthenticated, isLoading, logout } = useAuthStore();
  const sseConnectionRef = useRef<EventSource | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    const activeToken = token || localStorage.getItem('auth_token');

    if (activeToken) {
      // Connect to SSE for real-time logout events
      connectSSE(activeToken);
      // Setup BroadcastChannel for same-origin tab sync
      setupBroadcastChannel();
    }

    // Setup storage event listener for cross-tab login detection
    setupStorageListener();

    // Setup focus listener to check for cookie changes (for cross-origin tabs)
    setupFocusListener();

    // Cleanup on unmount
    return () => {
      disconnectSSE();
      closeBroadcastChannel();
      cleanupStorageListener();
      cleanupFocusListener();
    };
  }, [token]);

  // Track last reload time to prevent infinite loops
  const lastReloadTimeRef = useRef<number>(0);
  const RELOAD_DEBOUNCE_MS = 5000; // Don't reload more than once per 5 seconds

  function connectSSE(tokenForSSE: string) {
    // Close existing connection if any
    disconnectSSE();

    try {
      // SSE endpoint for real-time auth events
      const sseUrl = `${AUTH_HUB_URL}/api/auth/events?token=${encodeURIComponent(tokenForSSE)}`;
      const eventSource = new EventSource(sseUrl);

      eventSource.addEventListener('auth:logout', () => {
        console.log('[Auth-Hub] 🔴 Logout event received, broadcasting and reloading...');
        // Broadcast to other same-origin tabs
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({ type: 'auth:logout', timestamp: Date.now() });
        }
        // Clear local state and redirect to login
        logout();
      });

      eventSource.addEventListener('auth:login', () => {
        const now = Date.now();
        const timeSinceLastReload = now - lastReloadTimeRef.current;

        // Prevent infinite reload loops by debouncing
        if (timeSinceLastReload < RELOAD_DEBOUNCE_MS) {
          console.log('[Auth-Hub] ⚠️ Login event debounced (too soon after last reload)');
          return;
        }

        console.log('[Auth-Hub] ✅ Login event received, broadcasting and reloading...');
        lastReloadTimeRef.current = now;

        // Broadcast to other same-origin tabs
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({ type: 'auth:login', timestamp: Date.now() });
        }
        // Force reload to show authenticated content
        window.location.reload();
      });

      eventSource.onerror = (error: Event) => {
        console.error('[Auth-Hub] SSE error:', error);
        // EventSource auto-reconnects
      };

      sseConnectionRef.current = eventSource;
      console.log('[Auth-Hub] SSE connected');
    } catch (error) {
      console.error('[Auth-Hub] Failed to connect SSE:', error);
    }
  }

  function disconnectSSE() {
    if (sseConnectionRef.current) {
      sseConnectionRef.current.close();
      sseConnectionRef.current = null;
      console.log('[Auth-Hub] SSE disconnected');
    }
  }

  function setupBroadcastChannel() {
    if (typeof BroadcastChannel === 'undefined') {
      console.warn('[Auth-Hub] BroadcastChannel not supported');
      return;
    }

    try {
      const channel = new BroadcastChannel('auth_events');

      channel.onmessage = (event) => {
        console.log('[Auth-Hub] BroadcastChannel message:', event.data);

        if (event.data.type === 'auth:logout') {
          // Another tab received SSE logout event, we should logout too
          logout();
        } else if (event.data.type === 'auth:login') {
          // Another tab received SSE login event, we should reload
          window.location.reload();
        }
      };

      broadcastChannelRef.current = channel;
      console.log('[Auth-Hub] BroadcastChannel initialized');
    } catch (error) {
      console.error('[Auth-Hub] BroadcastChannel failed:', error);
    }
  }

  function closeBroadcastChannel() {
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.close();
      broadcastChannelRef.current = null;
      console.log('[Auth-Hub] BroadcastChannel closed');
    }
  }

  // Storage event listener for cross-tab login detection
  const storageListenerRef = useRef<((e: StorageEvent) => void) | null>(null);

  function setupStorageListener() {
    const handleStorageChange = (e: StorageEvent) => {
      // Detect when auth_token is added in another tab
      if (e.key === 'auth_token' && e.newValue && !e.oldValue) {
        console.log('[Auth-Hub] 🟢 Token added in another tab, reloading...');
        window.location.reload();
      }
    };

    storageListenerRef.current = handleStorageChange;
    window.addEventListener('storage', handleStorageChange);
    console.log('[Auth-Hub] Storage listener initialized');
  }

  function cleanupStorageListener() {
    if (storageListenerRef.current) {
      window.removeEventListener('storage', storageListenerRef.current);
      storageListenerRef.current = null;
      console.log('[Auth-Hub] Storage listener cleaned up');
    }
  }

  // Focus event listener to check for cookie changes (cross-origin support)
  const focusListenerRef = useRef<(() => void) | null>(null);

  function setupFocusListener() {
    const handleFocus = () => {
      // When tab gains focus, check if cookie exists but we're not authenticated
      const cookieToken = document.cookie.split(';').find(c => c.trim().startsWith('auth_token='));
      const hasToken = !!cookieToken;
      const currentToken = localStorage.getItem('auth_token');

      // If cookie exists but we don't have it in localStorage, someone logged in elsewhere
      if (hasToken && !currentToken && !isAuthenticated) {
        console.log('[Auth-Hub] 🟢 Cookie detected on focus, reloading...');
        window.location.reload();
      }
    };

    focusListenerRef.current = handleFocus;
    window.addEventListener('focus', handleFocus);
    console.log('[Auth-Hub] Focus listener initialized');
  }

  function cleanupFocusListener() {
    if (focusListenerRef.current) {
      window.removeEventListener('focus', focusListenerRef.current);
      focusListenerRef.current = null;
      console.log('[Auth-Hub] Focus listener cleaned up');
    }
  }

  return {
    user,
    token,
    isAuthenticated,
    isLoading,
    logout,
    canAccessAdmin: user?.role === 'admin',
  };
}
